import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, MagnifyingGlass } from "@phosphor-icons/react";

const empty = () => ({ code: "", name: "", quantity: 1, _new: true });

export default function WorkTools() {
  const { user } = useAuth();
  const canEdit = ["admin", "manager"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [dirty, setDirty] = useState({});

  const load = useCallback(async () => {
    try { setRows((await api.get("/safety/tools")).data); setDirty({}); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setCell = (idx, key, value) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [key]: value };
    setRows(next);
    setDirty({ ...dirty, [idx]: true });
  };

  const saveRow = async (idx) => {
    const r = rows[idx];
    if (!r.code || !r.name) return toast.error("Codice e descrizione sono obbligatori");
    try {
      const body = { code: r.code, name: r.name, quantity: Number(r.quantity || 1), size: "", notes: "", active: true };
      if (r.id) await api.put(`/safety/tools/${r.id}`, body);
      else await api.post("/safety/tools", body);
      toast.success("Attrezzo salvato");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (r, idx) => {
    if (!r.id) return setRows(rows.filter((_, i) => i !== idx));
    try { await api.delete(`/safety/tools/${r.id}`); toast.success("Attrezzo archiviato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const visible = rows.map((r, idx) => ({ r, idx }))
    .filter(({ r }) => !q || `${r.code} ${r.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="work-tools-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Attrezzature da Lavoro</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Coltelli, acciaino, ganci e simili: solo codice, descrizione e quantità. Sono gli articoli
            consegnabili nel modulo di consegna DPI.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" className="rounded-sm font-semibold" data-testid="tool-add"
            onClick={() => setRows([...rows, empty()])}>
            <Plus size={15} className="mr-1.5" /> Nuovo attrezzo
          </Button>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card p-3 mobile-stack flex flex-wrap gap-2 items-center">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Codice o descrizione..."
            className="h-9 w-64 pl-8 rounded-sm" data-testid="tools-search" />
        </div>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="tools-count">{rows.length} attrezzi</span>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="tools-table">
          <thead className="bg-secondary"><tr>
            {["", "Codice", "Descrizione", "Quantità"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {visible.map(({ r, idx }) => (
              <tr key={r.id || `new-${idx}`} className="border-t border-border" data-testid={`tool-row-${r.code || idx}`}>
                <td className="px-2 py-1 text-left whitespace-nowrap">
                  {canEdit && (
                    <>
                      <button className={`mr-3 ${dirty[idx] ? "text-primary" : "text-muted-foreground"} hover:text-primary`}
                        title="Salva" data-testid={`tool-save-${idx}`} onClick={() => saveRow(idx)}>
                        <FloppyDisk size={16} />
                      </button>
                      <button className="text-muted-foreground hover:text-rose-500" title="Elimina"
                        data-testid={`tool-delete-${idx}`} onClick={() => del(r, idx)}>
                        <Trash size={15} />
                      </button>
                    </>
                  )}
                </td>
                <td className="px-2 py-1">
                  <Input value={r.code || ""} disabled={!canEdit} onChange={(e) => setCell(idx, "code", e.target.value.toUpperCase())}
                    className="h-8 rounded-sm font-semibold" data-testid={`tool-code-${idx}`} />
                </td>
                <td className="px-2 py-1">
                  <Input value={r.name || ""} disabled={!canEdit} onChange={(e) => setCell(idx, "name", e.target.value)}
                    className="h-8 rounded-sm" data-testid={`tool-name-${idx}`} />
                </td>
                <td className="px-2 py-1 w-28">
                  <Input value={r.quantity ?? 1} disabled={!canEdit} onChange={(e) => setCell(idx, "quantity", e.target.value)}
                    className="h-8 rounded-sm tabular-nums" data-testid={`tool-qty-${idx}`} />
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground" data-testid="tools-empty">Nessun attrezzo</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
